import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "./auth-context";
import { AuthLayout } from "./auth-layout";
import { SocialAuthButtons } from "./social-auth";
import { Alert, Button, Input } from "@/components/ui";
import { ApiError } from "@/api/client";

export function LoginPage() {
  const { t } = useTranslation();
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Post-auth destination (e.g. an invite link round-tripping through login).
  // Restricted to internal paths to avoid an open redirect.
  const nextParam = params.get("next");
  const next = nextParam && nextParam.startsWith("/") ? nextParam : "/app";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === "authenticated") return <Navigate to={next} replace />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login({ email, password });
      navigate(next, { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? t("Incorrect email or password.")
          : err instanceof Error
            ? err.message
            : t("Something went wrong. Please try again."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title={t("Welcome back")}
      subtitle={t("Sign in to your EduPause account.")}
      footer={
        <>
          {t("New here?")}{" "}
          <Link to="/register" className="font-semibold text-brand-600 hover:underline">
            {t("Create an account")}
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        {error && <Alert>{error}</Alert>}
        <Input
          label={t("Email")}
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
        />
        <Input
          label={t("Password")}
          name="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
        />
        <div className="text-right">
          <Link
            to="/forgot-password"
            className="text-sm font-medium text-brand-600 hover:underline"
          >
            {t("Forgot your password?")}
          </Link>
        </div>
        <Button type="submit" size="lg" loading={submitting} className="w-full">
          {t("Sign in")}
        </Button>
      </form>
      <SocialAuthButtons next={next} onError={setError} />
    </AuthLayout>
  );
}
